import React, { useState, useEffect } from 'react';
import { Milestone, AttachedDocument } from '../types';
import { X } from 'lucide-react';
import { WarningIcon, InsightIcon, CheckIcon, AnalyticsIcon, GoalIcon, DocumentIcon, TrendIcon, GrowthIcon, MilestoneIcon, ClipboardIcon } from '../../../icons';
import DocumentUpload from '../components/DocumentUpload';
import { uploadMilestoneDocument, deleteDocument } from '../firebase/documents.service';
import { updateMilestone } from '../firebase/milestones.service';

interface MilestoneEditModalProps {
  milestone: Milestone;
  goalId: string;
  ownerId: string;
  goalStartDate: Date;
  goalEndDate: Date;
  onSave: (updated: Partial<Milestone>) => Promise<void>;
  onClose: () => void;
}

// Milestone category detection and info
type MilestoneCategory = 'learn' | 'plan' | 'build' | 'grow' | 'goal' | 'unknown';

const CATEGORY_INFO: Record<MilestoneCategory, {
  label: string;
  icon: React.ReactNode;
  color: string;
  bgColor: string;
  borderColor: string;
  description: string;
  idealPosition: string;
  keywords: string[];
}> = {
  learn: {
    label: 'Leerdoel',
    icon: '🎓',
    color: 'text-blue-700',
    bgColor: 'bg-blue-50',
    borderColor: 'border-blue-200',
    description: 'Kennis opdoen, cursussen volgen, onderzoeken. Dit is de basis waarop je bouwt.',
    idealPosition: 'Begin van het traject (eerste 20%)',
    keywords: ['leren', 'cursus', 'training', 'studeren', 'onderzoeken', 'kennis', 'certificaat', 'opleiding', 'workshop', 'boek', 'lees'],
  },
  plan: {
    label: 'Planningsdoel',
    icon: <ClipboardIcon size={16} />,
    color: 'text-purple-700',
    bgColor: 'bg-purple-50',
    borderColor: 'border-purple-200',
    description: 'Strategie uitwerken, plannen maken, structuur opzetten. De blauwdruk voor actie.',
    idealPosition: 'Vroeg in het traject (15-35%)',
    keywords: ['plan', 'strategie', 'opzetten', 'voorbereiden', 'ontwerpen', 'bepalen', 'roadmap', 'concept'],
  },
  build: {
    label: 'Uitvoeringsdoel',
    icon: '🔨',
    color: 'text-orange-700',
    bgColor: 'bg-orange-50',
    borderColor: 'border-orange-200',
    description: 'Bouwen, maken, lanceren. Actie ondernemen en resultaten creëren.',
    idealPosition: 'Midden van het traject (30-60%)',
    keywords: ['bouwen', 'maken', 'lanceren', 'starten', 'creëren', 'ontwikkelen', 'implementeren', 'verkopen', 'werven'],
  },
  grow: {
    label: 'Groeidoel',
    icon: <TrendIcon size={16} />,
    color: 'text-green-700',
    bgColor: 'bg-green-50',
    borderColor: 'border-green-200',
    description: 'Uitbreiden, schalen, optimaliseren. Groei ontstaat wanneer de fundering staat.',
    idealPosition: 'Tweede helft van het traject (55-90%)',
    keywords: ['groeien', 'uitbreiden', 'schalen', 'verdubbelen', 'optimaliseren', 'team', 'aannemen', 'delegeren', 'automatiseren'],
  },
  goal: {
    label: 'Einddoel',
    icon: <GoalIcon size={16} />,
    color: 'text-red-700',
    bgColor: 'bg-red-50',
    borderColor: 'border-red-200',
    description: 'Het ultieme resultaat waar alles naartoe werkt.',
    idealPosition: 'Einde van het traject (95-100%)',
    keywords: ['einddoel', 'ultiem', 'bereiken', 'target', 'visie', 'missie', 'succes'],
  },
  unknown: {
    label: 'Algemeen',
    icon: '📌',
    color: 'text-gray-700',
    bgColor: 'bg-gray-50',
    borderColor: 'border-gray-200',
    description: 'Een mijlpaal op weg naar je doel.',
    idealPosition: 'Afhankelijk van de inhoud',
    keywords: [],
  },
};

function detectMilestoneCategory(title: string): MilestoneCategory {
  const titleLower = title.toLowerCase();
  
  for (const [category, info] of Object.entries(CATEGORY_INFO)) {
    if (category === 'unknown') continue;
    if (info.keywords.some(kw => titleLower.includes(kw))) {
      return category as MilestoneCategory;
    }
  }
  
  return 'unknown';
}

function calculatePositionPercent(targetDate: string, goalStart: Date, goalEnd: Date): number | null {
  if (!targetDate) return null;
  
  const date = new Date(targetDate);
  const totalDuration = goalEnd.getTime() - goalStart.getTime();
  const elapsed = date.getTime() - goalStart.getTime();
  
  return Math.round((elapsed / totalDuration) * 100);
}

function isPositionLogical(category: MilestoneCategory, positionPercent: number | null): boolean {
  if (positionPercent === null) return true; // No date = can't judge
  
  const idealRanges: Record<MilestoneCategory, { min: number; max: number }> = {
    learn: { min: 0, max: 35 },
    plan: { min: 5, max: 45 },
    build: { min: 20, max: 75 },
    grow: { min: 40, max: 95 },
    goal: { min: 80, max: 100 },
    unknown: { min: 0, max: 100 },
  };
  
  const range = idealRanges[category];
  return positionPercent >= range.min && positionPercent <= range.max;
}

/**
 * MilestoneEditModal - Modal voor het bewerken van een mijlpaal
 * 
 * Features:
 * - Titel bewerken
 * - Datum aanpassen (binnen goal grenzen)
 * - Betekenis/beschrijving bewerken
 * - Periode aanpassen
 * - Category detection met logische positie waarschuwingen
 */
const MilestoneEditModal: React.FC<MilestoneEditModalProps> = ({
  milestone,
  goalId,
  ownerId,
  goalStartDate,
  goalEndDate,
  onSave,
  onClose,
}) => {
  const [title, setTitle] = useState(milestone.title);
  const [targetDate, setTargetDate] = useState(milestone.targetDate || '');
  const [meaning, setMeaning] = useState(milestone.meaning || '');
  const [globalPeriod, setGlobalPeriod] = useState(milestone.globalPeriod || '');
  const [documents, setDocuments] = useState<AttachedDocument[]>(milestone.documents || []);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showCategoryInfo, setShowCategoryInfo] = useState(false);
  
  // Detect category and position logic
  const category = detectMilestoneCategory(title);
  const categoryInfo = CATEGORY_INFO[category];
  const positionPercent = calculatePositionPercent(targetDate, goalStartDate, goalEndDate);
  const isLogical = isPositionLogical(category, positionPercent);

  // Format dates for input
  const minDate = goalStartDate.toISOString().split('T')[0];
  const maxDate = goalEndDate.toISOString().split('T')[0];

  // Suggested periods based on goal duration
  const suggestedPeriods = getSuggestedPeriods(goalStartDate, goalEndDate);

  // Check if date is valid (within goal range or empty)
  const isDateValid = !targetDate || (targetDate >= minDate && targetDate <= maxDate);
  
  const handleSave = async () => {
    if (!title.trim()) {
      setError('Titel is verplicht');
      return;
    }
    
    // Validate date is within goal boundaries
    if (targetDate && targetDate < minDate) {
      setError(`Datum moet op of na ${formatDateShort(goalStartDate)} zijn`);
      return;
    }
    
    if (targetDate && targetDate > maxDate) {
      setError(`Datum moet op of voor ${formatDateShort(goalEndDate)} zijn`);
      return;
    }

    setIsSaving(true);
    setError(null);

    try {
      await onSave({
        title: title.trim(),
        targetDate,
        meaning: meaning.trim(),
        globalPeriod: globalPeriod.trim(),
        documents,
      });
    } catch (err) {
      console.error('Failed to save milestone:', err);
      setError('Kon mijlpaal niet opslaan. Probeer opnieuw.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleDocumentUpload = async (file: File) => {
    const doc = await uploadMilestoneDocument(ownerId, goalId, milestone.id, file);
    const newDocs = [...documents, doc];
    setDocuments(newDocs);
    
    // Also save immediately to Firebase
    await updateMilestone(milestone.id, { documents: newDocs });
  };

  const handleDocumentDelete = async (docId: string) => {
    const docToDelete = documents.find(d => d.id === docId);
    if (docToDelete) {
      await deleteDocument(docToDelete.url);
    }
    
    const newDocs = documents.filter(d => d.id !== docId);
    setDocuments(newDocs);
    
    // Also save immediately to Firebase
    await updateMilestone(milestone.id, { documents: newDocs });
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      onClose();
    }
  };

  // Close on backdrop click
  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  return (
    <div 
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
      onClick={handleBackdropClick}
      onKeyDown={handleKeyDown}
    >
      <div className="bg-white rounded-xl shadow-xl max-w-md w-full max-h-[90dvh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">
            Mijlpaal bewerken
          </h3>
          <button
            onClick={onClose}
            className="p-1 hover:bg-gray-100 rounded-full transition-colors"
          >
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        {/* Content */}
        <div className="p-4 space-y-4">
          {/* Category Info Banner - Always visible, expandable */}
          <div className={`rounded-lg border-2 ${categoryInfo.borderColor} ${categoryInfo.bgColor} overflow-hidden`}>
            {/* Clickable header */}
            <button
              type="button"
              onClick={() => setShowCategoryInfo(!showCategoryInfo)}
              className={`w-full px-3 py-2 flex items-center justify-between ${categoryInfo.color} hover:opacity-80 transition-opacity`}
            >
              <div className="flex items-center gap-2">
                <span className="text-lg">{categoryInfo.icon}</span>
                <span className="font-medium">{categoryInfo.label}</span>
                {!isLogical && (
                  <span className="px-2 py-0.5 bg-yellow-400 text-yellow-900 text-xs font-bold rounded-full animate-pulse inline-flex items-center gap-1">
                    <WarningIcon size={12} className="text-yellow-900" /> Mogelijk verkeerde positie
                  </span>
                )}
              </div>
              <span className="text-sm">
                {showCategoryInfo ? '▲ Verberg info' : '▼ Meer info'}
              </span>
            </button>
            
            {/* Expandable content */}
            {showCategoryInfo && (
              <div className="px-3 py-3 border-t border-current/10 space-y-3">
                {/* Description */}
                <div>
                  <p className={`text-sm ${categoryInfo.color}`}>
                    {categoryInfo.description}
                  </p>
                </div>
                
                {/* Ideal position */}
                <div className="flex items-start gap-2">
                  <MilestoneIcon size={14} className="text-gray-600 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-gray-700">Ideale positie:</p>
                    <p className="text-sm text-gray-600">{categoryInfo.idealPosition}</p>
                  </div>
                </div>
                
                {/* Current position */}
                {positionPercent !== null && (
                  <div className="flex items-start gap-2">
                    <AnalyticsIcon size={14} className="text-gray-600 mt-0.5" />
                    <div>
                      <p className="text-sm font-medium text-gray-700">Huidige positie:</p>
                      <p className={`text-sm ${isLogical ? 'text-green-600' : 'text-yellow-600 font-medium'}`}>
                        {positionPercent}% van je traject
                        {isLogical ? <> <CheckIcon size={14} className="text-green-600 inline" /> Past goed</> : <> <WarningIcon size={14} className="text-yellow-600 inline" /> Mogelijk te vroeg of te laat</>}
                      </p>
                    </div>
                  </div>
                )}
                
                {/* Warning with suggestion */}
                {!isLogical && (
                  <div className="mt-2 p-2 bg-yellow-100 border border-yellow-300 rounded-lg">
                    <p className="text-sm text-yellow-800 font-medium flex items-center gap-1"><InsightIcon size={14} className="text-yellow-800" /> Suggestie:</p>
                    <p className="text-sm text-yellow-700 mt-1">
                      {category === 'learn' && positionPercent && positionPercent > 35 && (
                        <>Leerdoelen horen meestal vroeg in het traject. Overweeg de datum te vervroegen, of pas de titel aan als dit geen leerdoel is.</>
                      )}
                      {category === 'grow' && positionPercent && positionPercent < 40 && (
                        <>Groeidoelen horen meestal in de tweede helft van het traject, wanneer de fundering staat. Overweeg de datum later te zetten, of begin eerst met bouwen.</>
                      )}
                      {category === 'goal' && positionPercent && positionPercent < 80 && (
                        <>Je einddoel staat vrij vroeg. Zorg dat je genoeg tijd hebt om ernaartoe te werken.</>
                      )}
                      {category !== 'learn' && category !== 'grow' && category !== 'goal' && (
                        <>Controleer of de timing van deze mijlpaal logisch is in relatie tot je andere doelen.</>
                      )}
                    </p>
                  </div>
                )}
                
                {/* Process overview */}
                <div className="mt-2 pt-2 border-t border-current/10">
                  <p className="text-xs text-gray-500 font-medium mb-2">Logische volgorde in een traject:</p>
                  <div className="flex items-center gap-1 text-xs flex-wrap">
                    <span className={`px-2 py-1 rounded inline-flex items-center gap-1 ${category === 'learn' ? 'bg-blue-200 font-bold' : 'bg-gray-100'}`}><InsightIcon size={12} className="text-blue-700" /> Leren</span>
                    <span className="text-gray-400">→</span>
                    <span className={`px-2 py-1 rounded inline-flex items-center gap-1 ${category === 'plan' ? 'bg-purple-200 font-bold' : 'bg-gray-100'}`}><DocumentIcon size={12} className="text-purple-700" /> Plannen</span>
                    <span className="text-gray-400">→</span>
                    <span className={`px-2 py-1 rounded inline-flex items-center gap-1 ${category === 'build' ? 'bg-orange-200 font-bold' : 'bg-gray-100'}`}><GrowthIcon size={12} className="text-orange-700" /> Bouwen</span>
                    <span className="text-gray-400">→</span>
                    <span className={`px-2 py-1 rounded inline-flex items-center gap-1 ${category === 'grow' ? 'bg-green-200 font-bold' : 'bg-gray-100'}`}><TrendIcon size={12} className="text-green-700" /> Groeien</span>
                    <span className="text-gray-400">→</span>
                    <span className={`px-2 py-1 rounded inline-flex items-center gap-1 ${category === 'goal' ? 'bg-red-200 font-bold' : 'bg-gray-100'}`}><GoalIcon size={12} className="text-red-700" /> Doel</span>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Title */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Titel <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Wat wil je bereiken?"
              disabled={isSaving}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent disabled:bg-gray-50"
            />
          </div>

          {/* Target Date */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Datum
            </label>
            <input
              type="date"
              value={targetDate}
              onChange={(e) => {
                setTargetDate(e.target.value);
                setError(null); // Clear error when user changes date
              }}
              min={minDate}
              max={maxDate}
              disabled={isSaving}
              className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent disabled:bg-gray-50 ${
                !isDateValid ? 'border-red-500 bg-red-50' : 'border-gray-300'
              }`}
            />
            {!isDateValid && (
              <p className="text-xs text-red-600 mt-1 font-medium flex items-center gap-1">
                <WarningIcon size={14} className="text-red-600" /> Datum valt buiten je doelperiode!
              </p>
            )}
            <p className="text-xs text-gray-500 mt-1">
              Binnen de grenzen van je hoofddoel: {formatDateShort(goalStartDate)} - {formatDateShort(goalEndDate)}
            </p>
          </div>

          {/* Global Period */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Periode
            </label>
            <input
              type="text"
              value={globalPeriod}
              onChange={(e) => setGlobalPeriod(e.target.value)}
              placeholder="bijv. 'Over 3 maanden', 'Q2 2026'"
              disabled={isSaving}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent disabled:bg-gray-50"
            />
            {suggestedPeriods.length > 0 && (
              <div className="flex flex-wrap gap-1 mt-2">
                {suggestedPeriods.map((period) => (
                  <button
                    key={period}
                    type="button"
                    onClick={() => setGlobalPeriod(period)}
                    className="px-2 py-1 text-xs bg-orange-50 text-orange-600 rounded-full hover:bg-orange-100 transition-colors"
                  >
                    {period}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Meaning */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Betekenis / Beschrijving
            </label>
            <textarea
              value={meaning}
              onChange={(e) => setMeaning(e.target.value)}
              placeholder="Waarom is deze mijlpaal belangrijk? Wat betekent het voor je?"
              rows={4}
              disabled={isSaving}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent disabled:bg-gray-50"
            />
          </div>

          {/* Documents */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Documenten
            </label>
            <DocumentUpload
              documents={documents}
              onUpload={handleDocumentUpload}
              onDelete={handleDocumentDelete}
              disabled={isSaving}
            />
          </div>

          {/* Error */}
          {error && (
            <p className="text-sm text-red-600">{error}</p>
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-2 p-4 border-t border-gray-200 bg-gray-50 rounded-b-xl">
          <button
            onClick={onClose}
            disabled={isSaving}
            className="px-4 py-2 text-gray-600 hover:text-gray-800 disabled:opacity-50"
          >
            Annuleren
          </button>
          <button
            onClick={handleSave}
            disabled={!title.trim() || isSaving || !isDateValid}
            className="px-4 py-2 bg-orange-500 text-white rounded-lg font-medium hover:bg-orange-600 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
          >
            {isSaving ? 'Opslaan...' : 'Opslaan'}
          </button>
        </div>
      </div>
    </div>
  );
};

/**
 * Generate suggested periods based on goal duration
 */
function getSuggestedPeriods(start: Date, end: Date): string[] {
  const periods: string[] = [];
  const today = new Date();
  
  // Calculate months between start and end
  const monthsDiff = (end.getFullYear() - start.getFullYear()) * 12 + (end.getMonth() - start.getMonth());

  // Add relative periods
  if (monthsDiff >= 1) periods.push('Deze week');
  if (monthsDiff >= 3) periods.push('Over 3 maanden');
  if (monthsDiff >= 6) periods.push('Over 6 maanden');
  if (monthsDiff >= 12) periods.push('Over 1 jaar');

  // Add quarters if goal spans multiple quarters
  const startQuarter = Math.floor(start.getMonth() / 3) + 1;
  const endQuarter = Math.floor(end.getMonth() / 3) + 1;
  
  if (start.getFullYear() !== end.getFullYear() || startQuarter !== endQuarter) {
    let year = today.getFullYear();
    let quarter = Math.floor(today.getMonth() / 3) + 1;
    
    for (let i = 0; i < 4 && periods.length < 8; i++) {
      const qLabel = `Q${quarter} ${year}`;
      if (!periods.includes(qLabel)) {
        periods.push(qLabel);
      }
      quarter++;
      if (quarter > 4) {
        quarter = 1;
        year++;
      }
    }
  }

  return periods.slice(0, 6);
}

function formatDateShort(date: Date): string {
  return date.toLocaleDateString('nl-NL', { day: 'numeric', month: 'short', year: 'numeric' });
}

export default MilestoneEditModal;
